import React, { useEffect } from 'react';
import QRcode from './Components/QRCode';
import { BrowserRouter, MemoryRouter, Routes, Route } from 'react-router-dom';

function App() {
  const isBootstrapHosted =
    typeof window !== 'undefined' &&
    (
      window.__TRILL_PLATFORM__ === 'brightsign' ||
      window.location?.protocol === 'file:'
    );
  const Router = isBootstrapHosted ? MemoryRouter : BrowserRouter;
  
  // Dynamic viewport units setup
  useEffect(() => {
    const setViewportUnits = () => {
      const vh = window.innerHeight * 0.01;
      const vw = window.innerWidth * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
      document.documentElement.style.setProperty('--vw', `${vw}px`);
      
      // Log viewport dimensions for debugging (development only)
      if (process.env.NODE_ENV === 'development') {
        console.log(`[App] Viewport dimensions updated: ${window.innerWidth}x${window.innerHeight}`);
      }
    };

    // Set initial values
    setViewportUnits();
    
    // Update on resize or orientation change
    window.addEventListener('resize', setViewportUnits);
    window.addEventListener('orientationchange', setViewportUnits);
    
    // Also update when visibility changes (some TVs need this)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        setViewportUnits();
      }
    });
    
    return () => {
      window.removeEventListener('resize', setViewportUnits);
      window.removeEventListener('orientationchange', setViewportUnits);
    };
  }, []);
  
  // Content fit system - detects and scales overflowing content
  useEffect(() => {
    let resizeTimeoutId = null; // Track resize timeout for debouncing

    const ensureContentVisibility = () => {
      const container = document.querySelector('.main_wrapper');
      if (!container) return;

      // Reset any scaling
      container.style.transform = 'none';

      // Apply scaling if content is overflowing (more than 5% overflow)
      const contentHeight = container.scrollHeight;
      const availableHeight = window.innerHeight;
      const overflowRatio = contentHeight / availableHeight;

      // Scale if overflowing more than 105% of viewport (lowered from 120%)
      if (overflowRatio > 1.05) {
        const scaleFactor = Math.max(0.75, 0.98 / overflowRatio); // Allow down to 75%
        container.style.transform = `scale(${scaleFactor})`;
        container.style.transformOrigin = 'center top';
        // Reduced logging - only log in development
        if (process.env.NODE_ENV === 'development') {
          console.log(`[AutoFit] Applied scaling: ${(scaleFactor * 100).toFixed(1)}% for overflow ratio ${overflowRatio.toFixed(2)}`);
        }
      } else {
        // Reduced logging - only log in development
        if (process.env.NODE_ENV === 'development') {
          console.log('[AutoFit] Content fits naturally');
        }
      }
    };

    // Debounced resize handler - clears previous timeout before creating new one
    const handleResize = () => {
      if (resizeTimeoutId) {
        clearTimeout(resizeTimeoutId);
      }
      resizeTimeoutId = setTimeout(ensureContentVisibility, 100);
    };

    // Run quickly on mount (reduced delay from 500ms to 100ms)
    const timer = setTimeout(ensureContentVisibility, 100);
    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timer);
      if (resizeTimeoutId) {
        clearTimeout(resizeTimeoutId);
      }
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  // Note: Screensaver prevention now handled by useNoSleep hook in components

  return (
    <Router>
      <Routes>
        <Route path='/' element={<QRcode />} />
      </Routes>
    </Router>
  );
}

export default App;
